// Solana chain ID constant
const SOLANA_CHAIN_ID = 792703809

// Base58 alphabet (no 0, O, I, l)
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

/**
 * Validate EVM address (0x + 40 hex chars)
 */
export function isValidEvmAddress(address: string): boolean {
	if (!address || typeof address !== 'string') {
		return false
	}

	// EVM addresses are 0x followed by exactly 40 hex characters
	const evmRegex = /^0x[a-fA-F0-9]{40}$/
	return evmRegex.test(address)
}

/**
 * Validate Solana address (base58, 32-44 chars, no 0/O/I/l)
 */
export function isValidSolanaAddress(address: string): boolean {
	if (!address || typeof address !== 'string') {
		return false
	}

	// Solana addresses are 32-44 characters in base58
	if (address.length < 32 || address.length > 44) {
		return false
	}

	// Check that all characters are valid base58 (no 0, O, I, l)
	for (const char of address) {
		if (!BASE58_ALPHABET.includes(char)) {
			return false
		}
	}

	return true
}

/**
 * Validate address for specific chain ID
 * Solana chain ID is 792703809, all others are EVM
 */
export function isValidAddressForChain(address: string, chainId: number): boolean {
	if (chainId === SOLANA_CHAIN_ID) {
		return isValidSolanaAddress(address)
	}
	return isValidEvmAddress(address)
}

/**
 * Get hint text for address format
 * Returns "Solana address (base58)" or "EVM address (0x...)"
 */
export function getAddressFormatHint(chainId: number): string {
	if (chainId === SOLANA_CHAIN_ID) {
		return 'Solana address (base58)'
	}
	return 'EVM address (0x...)'
}

/**
 * Truncate address for display (e.g., "0x1234...5678")
 */
export function truncateAddress(address: string, chars = 4): string {
	if (!address || typeof address !== 'string') {
		return ''
	}

	if (address.length <= chars * 2 + 3) {
		return address
	}

	return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}
